import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { environment } from '@environments/environment';
import { AuthService, ProductService, UserService } from '@services';
import { catchError, EMPTY, firstValueFrom, map, Observable, of, ReplaySubject, switchMap, take, tap } from 'rxjs';
import { FAVORITE_DEFINITION_MAP } from './favorite-definition-map';
import { Favorite } from '@/shared/types/favorite';
import { FavoriteId } from '@/shared/enums/favorite-id.enum';

interface FavoriteDbResponse {
    userId: string;
    favorites: Favorite[];
}

@Injectable({ providedIn: 'root' })
export class FavoritesService {
    private http = inject(HttpClient);
    private authService = inject(AuthService);
    private productService = inject(ProductService);
    private userService = inject(UserService);

    private readonly favoritesApiUrl = environment.api.kepler.db.favorites.url;
    private cache$: ReplaySubject<Favorite[]> = new ReplaySubject(1);

    constructor() {
        this.sfdcUserId$
            .pipe(
                switchMap((userId) =>
                    this.http.get<FavoriteDbResponse>(this.favoritesApiUrl + '/' + userId, {
                        headers: {
                            Authorization: `Bearer ${this.authService.accessToken}`,
                            'csdt-authorization-app': 'braintab',
                            'Content-Type': 'application/json',
                        },
                    }),
                ),
                map((res) => this.adapt(res.favorites)),
                catchError((err) => {
                    console.error('Failed to load favorites', err);
                    return of([]);
                }),
            )
            .subscribe((favorites) => {
                console.log('loaded favorites', favorites);
                this.cache$.next(favorites);
            });
    }

    private get sfdcUserId$(): Observable<string | undefined> {
        return this.userService.sfdcUserDetail$.pipe(map((userSfdcRes) => userSfdcRes?.User?.Id));
    }

    /**
     * Adapts remote database result of a flat array of {@link Favorite}
     * to include additional information like label and url for UI functionality.
     * The database only stores the favoriteId and productId.
     * @param favorites to adapt
     * @returns Observable adapted favorites with additional properties
     */
    private adapt(favorites: Favorite[]): Favorite[] {
        const adapted = favorites
            .filter((favorite) => {
                return !this.isLegacyFavorite(favorite);
            })
            .map((favorite: Favorite): Favorite => {
                const definition = FAVORITE_DEFINITION_MAP[favorite.favoriteId];

                /*
                 * KNOW-3706: this bug resulted in some favorites from being saved
                 * to the db without their preferred label. So regardless of first
                 * or third party favorite, we always try the preferred label
                 * before falling back to the static label. If neither is present
                 * (which should never happen), we'll just put "Unknown".
                 */
                const label = favorite.label || definition.label || 'Unknown';

                if (definition) {
                    return {
                        ...favorite,
                        label,
                        routerLink: definition.routerLink,
                        fragment: definition.fragment,
                    };
                } else {
                    // Orchestrators dynamic favorites from third-party content
                    return {
                        ...favorite,
                        label,
                        routerLink: favorite.routerLink,
                        fragment: favorite.fragment,
                    };
                }
            });

        return adapted;
    }

    /**
     * @returns true when the given favorite is a Legacy GKN Favorite. These should be ignored. Legacy favorites did not include enough information about human-readible labels or routing information.
     */
    private isLegacyFavorite(favorite: any): boolean {
        return (
            Object.keys(favorite).length == 2 &&
            Object.keys(favorite).includes('favoriteId') &&
            Object.keys(favorite).includes('productId')
        );
    }

    /** @returns list of favorites for the currently selected product ordered by label DESC */
    get favorites$(): Observable<Favorite[]> {
        return this.cache$.asObservable().pipe(
            switchMap((cache) => {
                return this.productService.selectedProduct$.pipe(map((product) => ({ product, cache })));
            }),
            map(({ product, cache }) => {
                return cache
                    .filter((favorite) => {
                        return favorite?.productId === product.uid;
                    })
                    .sort((a, b) => {
                        return a?.label?.localeCompare(b?.label);
                    });
            }),
        );
    }

    /**
     * Finds the given {@link Favorite} for the currently selected
     * @param favoriteId to find, either a {@link FavoriteId} or a unique identifier from dynamic third-party content
     * @returns the favorite, if presetnt. otherwise undefined.
     */
    find(favoriteId: FavoriteId | string): Observable<Favorite | undefined> {
        return this.cache$.pipe(
            switchMap((cache) => {
                return this.productService.selectedProduct$.pipe(
                    map((selectedProduct) => {
                        return cache.find((cachedFavorite) => {
                            return (
                                cachedFavorite.productId === selectedProduct.uid &&
                                cachedFavorite.favoriteId === favoriteId
                            );
                        });
                    }),
                );
            }),
        );
    }

    /**
     * Add favorite to the remote database.
     *
     * Do not call this function directly. You must use the FavoriteButtonComponent due
     * to the nuances in creating the given {@link favorite}.
     *
     * The underlying Subject supporting {@link favorites$} is updated with
     * the HTTP POST response containing the full, updated list of favorites.
     *
     * The given {@link favoriteId} is NOT cross-referenced with the {@link FAVORITE_DEFINITION_MAP}.
     * This is intentional. By lacking a check in the definition map, we allow dynamic favoriting of
     * content based on a unique identifier, like a Wordpress Post title or ID, a specific Community
     * Youtube video, or any other content that is not hard-coded within source.
     *
     * @param favorite to add
     */
    add(favorite: Favorite): void {
        console.debug('new favorite being posted', favorite);

        this.sfdcUserId$
            .pipe(
                switchMap((userId) => {
                    const body = {
                        userId,
                        favorites: [favorite],
                    };

                    return this.http.post<FavoriteDbResponse>(this.favoritesApiUrl, body).pipe(
                        tap((res) => {
                            console.debug('favorites /POST response', res);
                        }),
                    );
                }),
                catchError((err) => {
                    const label = favorite.label;
                    console.error(`Failed to add ${label} to favorites`, { cause: err });
                    return EMPTY;
                }),
                take(1),
            )
            .subscribe((res) => {
                const filtered =
                    res?.favorites?.filter((favorite: any) => {
                        return !this.isLegacyFavorite(favorite);
                    }) || [];
                console.debug('new favorites cache', filtered);
                this.cache$.next(filtered);
            });
    }

    /**
     * Removes favorite from the remote database.
     * The underlying Subject supporting {@link favorites$} is updated with
     * the HTTP DELETE response containing the full, updated list of favorites.
     * @param favoriteId to remove
     */
    async remove(favoriteId: FavoriteId): Promise<void> {
        const toRemove = await firstValueFrom(this.find(favoriteId));
        if (!toRemove) {
            console.error(`Failed to remove favorite ${favoriteId}. Favorite not found.`);
            return;
        }

        this.sfdcUserId$
            .pipe(
                switchMap((userId) => {
                    const body = {
                        userId,
                        favorites: [{ favoriteId: toRemove.favoriteId, productId: toRemove.productId }],
                    };

                    return this.http
                        .delete<FavoriteDbResponse>(this.favoritesApiUrl, { body })
                        .pipe(map((res) => ({ res, label: toRemove.label })));
                }),
                catchError((err) => {
                    console.error(`Failed to remove ${toRemove.label} from favorites`, { cause: err });
                    return EMPTY;
                }),
                take(1),
            )
            .subscribe(({ res }) => {
                console.debug('Favorites /DELETE response', res);

                const filtered =
                    res?.favorites?.filter((favorite: any) => {
                        return !this.isLegacyFavorite(favorite);
                    }) || [];
                this.cache$.next(filtered);
            });
    }

    /**
     * Copies the absolute path to the favorite-able resource.
     * Some favorites require a fragment to support anchor scrolling, while others are child pages of root pages.
     * Note: the user can only toggle a favorite from the page where the content lives,
     * so window.location.href is utilized for simplicity.
     *
     * @param favorite to copy
     */
    async copyFavoriteUrl(favorite: Favorite): Promise<void> {
        const favoriteDefinition = FAVORITE_DEFINITION_MAP[favorite.favoriteId];
        if (favoriteDefinition) {
            // static content in source
            const url = new URL(window.location.origin);
            url.pathname = favorite.routerLink;
            if (favorite.fragment) url.hash = favorite.fragment;
        } else {
            // dynamic third-party content

            let url: URL;
            if (window.location.pathname.endsWith(favorite.routerLink)) {
                url = new URL(window.location.href);
            } else {
                url = new URL(window.location.origin);
                url.pathname = favorite.routerLink;
            }

            if (favorite.fragment) url.hash = favorite.fragment;
        }
    }
}
